import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import type { Announcements, DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import { SortableArea, useRowSortable, useSortableKit } from '@/components/lazySortable';
import { sayReorder } from '@/components/reorderWords';
import { GripVertical, Plus, X } from 'lucide-react';
import type { IWorkAreaDashboardPage } from '@featit/shared';
import {
  buildPageTree,
  MAX_PAGE_TREE_DEPTH,
  PAGE_TREE_INDENT_STEP_PX,
  pageAncestorIds,
  type IPageTreeNode,
} from '@featit/shared/page-tree';
import { NavFoldGlyph } from '@/components/NavFoldGlyph';
import { Skeleton } from '@/components/Skeleton';
import { gateControlProps } from '@/utils/gateControl';
import {
  heldGate,
  type IAccessRead,
  type IGatedWorkArea,
  type WorkAreaGateType,
} from '@/utils/workAreaPermissionGate';
import { PageIcon } from './PageIcon';
import { projectDashboardPagePath } from './dashboardUrls';
import { readExpandedPageIds, writeExpandedPageIds } from './pageTreeExpansion';
import { useT } from '@/i18n/LocaleProvider';
import { usePackageNamesOf } from '@/features/packages/packageNames';

export interface PageNavigationListProps {
  /** The work area's custom pages, in the order the server gave. Never re-sorted here (P-4). */
  pages: IWorkAreaDashboardPage[];
  /** Which work area this tree belongs to: what is expanded is remembered per work area (AC-7). */
  workAreaId: string;
  projectSlug: string | null | undefined;
  /** The page being read, so its branch is opened whatever the reader last collapsed (AC-8). */
  currentPageId?: string | null;
  /**
   * Whether the reader may manage pages here (`work_area.manage`), `undefined` until the server says.
   * F-0324: without it the add and reorder controls are shown disabled, never left out.
   */
  canManage: boolean | undefined;
  /** The read `canManage` comes from, so a failed one is not pending for good (I-1702). */
  manageRead?: IAccessRead;
  /**
   * D-0374 a (Jay): `false` for a guest-only person on a guest's work area, who is shown no
   * "New page under …" or reorder control at all — the one exception to F-0324's "disabled, never
   * left out". Everyone else gets them, disabled where they may not manage.
   */
  manageControls?: boolean;
  /** PX-1: the work area the pages are in, so a customer guest is refused in their words. */
  workArea?: IGatedWorkArea | null;
  reordering: boolean;
  orderError: string | undefined;
  /** One level's new order, and the parent that level belongs to — null for the top level. */
  onReorder: (pageIds: string[], parentPageId: string | null) => void;
  /** Move a page under another page, or to the top level with `null`. */
  onMove: (pageId: string, parentPageId: string | null) => void;
  /** Start creating a page under this one. */
  onCreateChild: (parentPageId: string) => void;
  onDismissOrderError: () => void;
  onNavigate: () => void;
  linkClassName: (state: { isActive: boolean }) => string;
  /**
   * The icon size of a row of the tree (AC-16, `concept-pages-and-documents.md` rule 12): the
   * row's text sets it — on the phone the line's own, on a desktop the tree's smaller one.
   */
  rowIconClassName?: string;
}

interface VisibleRow {
  node: IPageTreeNode<IWorkAreaDashboardPage>;
  hasChildren: boolean;
  expanded: boolean;
  /** The page's siblings, in order — the list a drag or Alt+↑/↓ reorders (AC-10). */
  siblingIds: string[];
  /** The page above it in the tree at the same level, which Alt+→ makes its new parent. */
  previousSiblingId: string | null;
}

/**
 * The tree of pages beneath Home in the sidebar (F-0186, F-0201).
 *
 * There is no caret column (I-1332): a page with children draws its fold chevron **in its icon's
 * place** (`NavFoldGlyph` — on hover or keyboard focus with a mouse, at rest with a finger), so every
 * icon and every title at one depth stands on the same vertical line whether or not the page folds.
 * Each level indents by one small step (`PAGE_TREE_INDENT_STEP_PX`) under its parent, with Home as
 * the top level's parent. The indent goes on the group inside the row rather than on the row, which
 * is what keeps the drag handle at the row's end flush at every depth.
 *
 * A manager reorders siblings by dragging the handle at the end of the row — revealed on hover or
 * focus, and a permanent 44px target where the pointer is coarse — or from the keyboard: with the
 * row or its handle focused, Alt+↑ and Alt+↓ move the page one place among its siblings, Alt+→
 * makes it a child of the page above it and Alt+← lifts it out to sit after its parent (I-1373),
 * and on the handle Space, the arrows and Space again drag it the way dnd-kit's keyboard sensor
 * does. Either way the whole new order of
 * that one level is saved at once. A reader without `work_area.manage` gets plain links and no
 * handles.
 *
 * The tree itself is a `tree` of `treeitem`s: ↑ and ↓ walk the rows that are drawn, → opens a
 * branch and then steps into it, ← closes it and then steps out to the parent, and Enter opens the
 * page.
 */
export function PageNavigationList({
  pages,
  workAreaId,
  projectSlug,
  currentPageId,
  canManage,
  manageRead,
  manageControls = true,
  workArea,
  reordering,
  orderError,
  onReorder,
  onMove,
  onCreateChild,
  onDismissOrderError,
  onNavigate,
  linkClassName,
  rowIconClassName = 'h-3.5 w-3.5',
}: PageNavigationListProps) {
  const t = useT();
  // I-1977: a package page's title in the reader's language, from the work area's package labels; a
  // title a person wrote reads as it is.
  const packageName = usePackageNamesOf(workAreaId);
  const navigate = useNavigate();
  const [announcement, setAnnouncement] = useState('');
  const refocusPageIdRef = useRef<string | null>(null);
  /** Whether that moved page was moved from its row itself (Alt+arrows on it), not its handle. */
  const refocusRowRef = useRef(false);
  const listRef = useRef<HTMLDivElement>(null);
  // ⭐ No stored state means every branch is collapsed (Jay, 2026-09-18) — and a `localStorage` that
  // throws reads as no stored state, so a private window looks exactly like a first visit (AC-7).
  const [expandedIds, setExpandedIds] = useState<string[]>(() => readExpandedPageIds(workAreaId));
  const [focusedPageId, setFocusedPageId] = useState<string | null>(null);
  // Mouse and touch apart, because they want different things (I-0395): `sensors="mouse-and-touch"`
  // below. T-0770 (LC-6a): dnd-kit is a chunk of its own; the pages draw without it until it is in.
  const sortableKit = useSortableKit();

  // Switching work area reads that work area's own memory. Keyed globally, one work area's open
  // branches would decide what another work area draws.
  //
  // Guarded by the work area it last read, and not merely by the dependency: an effect also runs on
  // mount, and one that read again there would overwrite the state above — making the first read
  // dead code, and the "no stored state means collapsed" rule unfalsifiable from it.
  const readWorkAreaRef = useRef(workAreaId);
  useEffect(() => {
    if (readWorkAreaRef.current === workAreaId) return;
    readWorkAreaRef.current = workAreaId;
    setExpandedIds(readExpandedPageIds(workAreaId));
  }, [workAreaId]);

  const expanded = useMemo(() => new Set(expandedIds), [expandedIds]);
  const tree = useMemo(() => buildPageTree(pages), [pages]);
  const byId = useMemo(() => new Map(pages.map((page) => [page.id, page])), [pages]);

  function remember(next: Set<string>): void {
    const ids = [...next];
    setExpandedIds(ids);
    writeExpandedPageIds(workAreaId, ids);
  }

  function toggle(pageId: string): void {
    const next = new Set(expanded);
    if (next.has(pageId)) next.delete(pageId);
    else next.add(pageId);
    remember(next);
  }

  // AC-8: the page being read is always visible, however deep it sits and whatever was collapsed.
  // This opens one path rather than everything — it is not a default, and what it opens is
  // remembered like any branch the reader opened by hand.
  useEffect(() => {
    if (!currentPageId) return;
    const ancestors = pageAncestorIds(pages, currentPageId);
    if (ancestors.length === 0) return;
    setExpandedIds((previous) => {
      if (ancestors.every((id) => previous.includes(id))) return previous;
      const next = [...new Set([...previous, ...ancestors])];
      writeExpandedPageIds(workAreaId, next);
      return next;
    });
  }, [currentPageId, pages, workAreaId]);

  /** The rows actually drawn: a branch that is collapsed contributes its own row and no more. */
  const rows = useMemo(() => {
    const out: VisibleRow[] = [];
    const walk = (nodes: IPageTreeNode<IWorkAreaDashboardPage>[]): void => {
      const siblingIds = nodes.map((node) => node.page.id);
      nodes.forEach((node, index) => {
        const hasChildren = node.children.length > 0;
        const isExpanded = hasChildren && expanded.has(node.page.id);
        out.push({
          node,
          hasChildren,
          expanded: isExpanded,
          siblingIds,
          previousSiblingId: index > 0 ? siblingIds[index - 1] : null,
        });
        if (isExpanded) walk(node.children);
      });
    };
    walk(tree);
    return out;
  }, [expanded, tree]);

  // A moved row is the same element in a new place, and moving a focused element can drop focus in
  // some browsers. Put it back on the row or the handle the reader was using, once the new order is
  // drawn.
  useEffect(() => {
    const id = refocusPageIdRef.current;
    if (!id) return;
    refocusPageIdRef.current = null;
    const selector = refocusRowRef.current
      ? `[data-page-treeitem="${id}"]`
      : `[data-page-handle="${id}"]`;
    refocusRowRef.current = false;
    listRef.current?.querySelector<HTMLElement>(selector)?.focus();
  }, [pages]);

  /** I-2453: a move's words load with the drag kit; one made before they are in waits for them. */
  const announce = (words: () => string) => sayReorder(() => setAnnouncement(words()));
  const titleOf = (id: UniqueIdentifier | string | undefined) => {
    const title = id ? byId.get(String(id))?.title : undefined;
    return title ? packageName(title) : t('reorder.pages.thePage');
  };

  /** Puts a page at `to` among its own siblings, and saves that one level's whole order. */
  function reorderTo(pageId: string, to: number): void {
    const row = rows.find((candidate) => candidate.node.page.id === pageId);
    if (!row) return;
    const from = row.siblingIds.indexOf(pageId);
    if (from < 0 || to < 0 || to >= row.siblingIds.length || from === to) return;
    const parentPageId = row.node.page.parentPageId ?? null;
    refocusPageIdRef.current = pageId;
    const position = { position: to + 1, count: row.siblingIds.length };
    announce(() =>
      parentPageId
        ? t('reorder.pages.movedToPositionUnder', {
            name: titleOf(pageId),
            ...position,
            parent: titleOf(parentPageId),
          })
        : t('reorder.movedToPosition', { name: titleOf(pageId), ...position }),
    );
    onReorder(movedTo(row.siblingIds, from, to), parentPageId);
  }

  /** Alt+↑ / Alt+↓: one place among its own siblings. */
  function move(pageId: string, offset: -1 | 1): void {
    const row = rows.find((candidate) => candidate.node.page.id === pageId);
    if (!row) return;
    reorderTo(pageId, row.siblingIds.indexOf(pageId) + offset);
  }

  /** Alt+→: the page becomes a child of the sibling above it, which is the outliner's indent. */
  function indent(pageId: string): void {
    const row = rows.find((candidate) => candidate.node.page.id === pageId);
    if (!row) return;
    if (!row.previousSiblingId) {
      announce(() => t('reorder.pages.firstAtLevel', { name: titleOf(pageId) }));
      return;
    }
    const previousSiblingId = row.previousSiblingId;
    if (row.node.level + 1 > MAX_PAGE_TREE_DEPTH) {
      announce(() =>
        t('reorder.pages.tooDeep', {
          name: titleOf(pageId),
          parent: titleOf(previousSiblingId),
          depth: MAX_PAGE_TREE_DEPTH,
        }),
      );
      return;
    }
    refocusPageIdRef.current = pageId;
    // The new parent has to be open, or the page the reader just moved would vanish from the tree.
    remember(new Set([...expanded, previousSiblingId]));
    announce(() =>
      t('reorder.pages.movedUnder', {
        name: titleOf(pageId),
        parent: titleOf(previousSiblingId),
      }),
    );
    onMove(pageId, previousSiblingId);
  }

  /** Alt+←: the page leaves its parent and joins the level above, after that parent. */
  function outdent(pageId: string): void {
    const page = byId.get(pageId);
    const parentPageId = page?.parentPageId ?? null;
    if (!parentPageId) {
      announce(() => t('reorder.alreadyTop', { name: titleOf(pageId) }));
      return;
    }
    const grandparentId = byId.get(parentPageId)?.parentPageId ?? null;
    refocusPageIdRef.current = pageId;
    announce(() =>
      grandparentId
        ? t('reorder.pages.movedOutUnder', {
            name: titleOf(pageId),
            parent: titleOf(parentPageId),
            grandparent: titleOf(grandparentId),
          })
        : t('reorder.pages.movedOutTop', { name: titleOf(pageId), parent: titleOf(parentPageId) }),
    );
    onMove(pageId, grandparentId);
  }

  /**
   * A drag reorders a page among its own siblings. A drop on a row at another level is refused and
   * said out loud rather than quietly re-parenting the page: a drag that changed which page
   * something lives under without saying so is the one thing this must not do (AC-10).
   */
  function handleDragEnd(event: DragEndEvent): void {
    if (!event.over || event.active.id === event.over.id) return;
    const activeId = String(event.active.id);
    const overId = String(event.over.id);
    const row = rows.find((candidate) => candidate.node.page.id === activeId);
    if (!row) return;
    const to = row.siblingIds.indexOf(overId);
    if (to < 0) {
      announce(() => t('reorder.pages.notAmongSiblings', { name: titleOf(activeId) }));
      return;
    }
    reorderTo(activeId, to);
  }

  const announcements: Announcements = {
    onDragStart: ({ active }) => t('reorder.pickedUp', { name: titleOf(active.id) }),
    onDragOver: ({ active, over }) =>
      over
        ? t('reorder.over', { name: titleOf(active.id), target: titleOf(over.id) })
        : t('reorder.pages.notOver', { name: titleOf(active.id) }),
    onDragEnd: ({ active, over }) =>
      over
        ? t('reorder.dropped', { name: titleOf(active.id), target: titleOf(over.id) })
        : t('reorder.droppedUnchanged', { name: titleOf(active.id) }),
    onDragCancel: ({ active }) => t('reorder.cancelled', { name: titleOf(active.id) }),
  };

  const manageGate = heldGate(canManage, 'work_area.manage', manageRead, workArea);
  /** The one row in the tab order: where the reader is, else the page being read, else the first. */
  const tabbablePageId =
    rows.find((row) => row.node.page.id === focusedPageId)?.node.page.id ??
    rows.find((row) => row.node.page.id === currentPageId)?.node.page.id ??
    rows[0]?.node.page.id ??
    null;

  function focusRow(pageId: string | null | undefined): void {
    if (!pageId) return;
    setFocusedPageId(pageId);
    listRef.current?.querySelector<HTMLElement>(`[data-page-treeitem="${pageId}"]`)?.focus();
  }

  /** ↑ ↓ → ← Enter over the tree, on the rows that are drawn (AC-9). */
  function onTreeKeyDown(event: KeyboardEvent<HTMLDivElement>, row: VisibleRow): void {
    const pageId = row.node.page.id;
    const index = rows.findIndex((candidate) => candidate.node.page.id === pageId);
    /*
     * I-1373 (AC-25 in concept-work-area-views-in-the-sidebar.md, the Views tree's rule): Alt+arrows
     * move the focused row itself, not only its handle — on a focused row Alt+↓ used to be a plain
     * ↓, so the focus went down a row and the order stayed. From the handle the handle has already
     * moved it, so the row only keeps the key from also reading as a plain arrow there.
     */
    if (event.altKey && event.key.startsWith('Arrow')) {
      event.preventDefault();
      if (event.target !== event.currentTarget) return;
      if (manageGate.state !== 'permitted') {
        announce(() => t('reorder.cannotMove', { name: titleOf(pageId) }));
        return;
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        if (!reordering) move(pageId, event.key === 'ArrowUp' ? -1 : 1);
      } else if (!reordering) {
        if (event.key === 'ArrowRight') indent(pageId);
        else outdent(pageId);
      }
      // Only a move that happened asks for focus back, and then on the row it started from.
      refocusRowRef.current = refocusPageIdRef.current === pageId;
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusRow(rows[index + 1]?.node.page.id);
        return;
      case 'ArrowUp':
        event.preventDefault();
        focusRow(rows[index - 1]?.node.page.id);
        return;
      case 'ArrowRight':
        event.preventDefault();
        if (row.hasChildren && !row.expanded) toggle(pageId);
        else if (row.hasChildren) focusRow(row.node.children[0]?.page.id);
        return;
      case 'ArrowLeft':
        event.preventDefault();
        if (row.hasChildren && row.expanded) toggle(pageId);
        else focusRow(row.node.page.parentPageId);
        return;
      case 'Home':
        event.preventDefault();
        focusRow(rows[0]?.node.page.id);
        return;
      case 'End':
        event.preventDefault();
        focusRow(rows[rows.length - 1]?.node.page.id);
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        onNavigate();
        navigate(projectDashboardPagePath(projectSlug, pageId));
        return;
      default:
    }
  }

  if (pages.length === 0 && !orderError) return null;

  return (
    <div ref={listRef} className="space-y-0.5">
      <SortableArea
        current={sortableKit}
        ids={rows.map((row) => row.node.page.id)}
        sensors="mouse-and-touch"
        onDragEnd={handleDragEnd}
        announcements={announcements}
        screenReaderInstructions={{
          draggable: t('reorder.pages.instructions'),
        }}
      >
        <div role="tree" aria-label={t('navigation.sidebar.pages')} className="space-y-0.5">
          {rows.map((row, index) => (
            <PageNavigationRow
              key={row.node.page.id}
              row={row}
              title={
                packageName.loading ? t('reorder.pages.thePage') : packageName(row.node.page.title)
              }
              titleWaiting={packageName.loading}
              accentPosition={index}
              to={projectDashboardPagePath(projectSlug, row.node.page.id)}
              manageGate={manageGate}
              manageControls={manageControls}
              reorderDisabled={!canManage || reordering}
              tabbable={row.node.page.id === tabbablePageId}
              onNavigate={onNavigate}
              linkClassName={linkClassName}
              iconClassName={rowIconClassName}
              onToggle={() => toggle(row.node.page.id)}
              onCreateChild={() => onCreateChild(row.node.page.id)}
              onMoveBy={(offset) => move(row.node.page.id, offset)}
              onIndent={() => indent(row.node.page.id)}
              onOutdent={() => outdent(row.node.page.id)}
              onTreeKeyDown={(event) => onTreeKeyDown(event, row)}
              onFocusRow={() => setFocusedPageId(row.node.page.id)}
            />
          ))}
        </div>
      </SortableArea>
      {/*
        I-0412: `sr-only` is `position: absolute`, so this element needs a positioned ancestor or it
        offsets against the document and scrolls the whole window. The sidebar's scroll box is
        `relative` for exactly this; nothing between here and it may be taken away.
      */}
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>
      {orderError ? (
        <div
          role="alert"
          className="ml-4 flex items-start gap-1.5 rounded-md bg-red-50 px-2 py-1.5 text-[11px] leading-4 text-red-700 dark:bg-red-950/40 dark:text-red-200"
        >
          <span className="min-w-0 flex-1">{orderError}</span>
          <button
            type="button"
            aria-label={t('navigation.sidebar.dismiss')}
            title={t('navigation.sidebar.dismiss')}
            onClick={onDismissOrderError}
            className="grid h-4 w-4 shrink-0 place-items-center rounded text-red-500 outline-none hover:bg-red-100 focus-visible:ring-2 focus-visible:ring-red-500/40 dark:text-red-300 dark:hover:bg-red-900/50"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

function PageNavigationRow({
  row,
  title,
  titleWaiting = false,
  to,
  manageGate,
  manageControls,
  reorderDisabled,
  tabbable,
  onNavigate,
  linkClassName,
  iconClassName,
  accentPosition,
  onToggle,
  onCreateChild,
  onMoveBy,
  onIndent,
  onOutdent,
  onTreeKeyDown,
  onFocusRow,
}: {
  row: VisibleRow;
  /** The page's title as drawn: a package page's in the reader's language (I-1977). */
  title: string;
  /** I-2088: the package's words are still on their way: a skeleton stands in for the title. */
  titleWaiting?: boolean;
  to: string;
  manageGate: WorkAreaGateType;
  manageControls: boolean;
  reorderDisabled: boolean;
  tabbable: boolean;
  onNavigate: () => void;
  linkClassName: (state: { isActive: boolean }) => string;
  iconClassName: string;
  /** F-0579 ACC-4a: the row's place in the tree as drawn, which picks its frame stop in Colour. */
  accentPosition: number;
  onToggle: () => void;
  onCreateChild: () => void;
  onMoveBy: (offset: -1 | 1) => void;
  onIndent: () => void;
  onOutdent: () => void;
  onTreeKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  onFocusRow: () => void;
}) {
  const t = useT();
  const { page, level } = row.node;
  const sortableDisabled = reorderDisabled || row.siblingIds.length < 2;
  const { attributes, listeners, setNodeRef, transformCss, transition, isDragging } =
    useRowSortable({ id: page.id, disabled: sortableDisabled });

  function onHandleKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (event.altKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      event.preventDefault();
      if (!sortableDisabled) onMoveBy(event.key === 'ArrowUp' ? -1 : 1);
      return;
    }
    if (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      // Indent and outdent are moves, not reorders: they stay available where a level holds one
      // page, which is exactly when a page most needs lifting out of it.
      if (!reorderDisabled) {
        if (event.key === 'ArrowRight') onIndent();
        else onOutdent();
      }
      return;
    }
    listeners?.onKeyDown?.(event);
  }

  return (
    <div
      ref={setNodeRef}
      role="treeitem"
      data-page-treeitem={page.id}
      data-page-level={level}
      aria-level={level}
      aria-posinset={row.siblingIds.indexOf(page.id) + 1}
      aria-setsize={row.siblingIds.length}
      aria-expanded={row.hasChildren ? row.expanded : undefined}
      tabIndex={tabbable ? 0 : -1}
      onKeyDown={onTreeKeyDown}
      onFocus={onFocusRow}
      style={{ transform: transformCss, transition }}
      // The row itself is the tab stop (roving tabindex), so it needs its own visible focus ring,
      // not only the drag handle's narrower one (I-1053/F-0411 — the same gap found in
      // ViewNavigationList.tsx's identical row under F-0363, fixed there and here through the one
      // shared class).
      // `nav-fold`: the row whose hover or focus turns its icon into the fold chevron (I-1332).
      className={`nav-fold rounded-md focus-ring-row ${isDragging ? 'relative z-10' : ''}`}
    >
      <div
        data-page-id={page.id}
        className={`group relative flex items-center rounded-md [@media(pointer:coarse)]:min-h-touch ${
          isDragging
            ? 'bg-[#F0F1F2] shadow-md ring-1 ring-black/5 dark:bg-[#2a2a2a] dark:ring-white/10'
            : ''
        }`}
      >
        {/*
          The indent lives on this group, not on the row: the row keeps the sidebar's full width at
          every depth, so the handle at its end stands in one place however deep the page sits. A
          top-level page is already one step in: it sits under Home.
        */}
        <div
          data-page-indent
          className="flex min-w-0 flex-1 items-center"
          style={{ paddingLeft: level * PAGE_TREE_INDENT_STEP_PX }}
        >
          <NavLink
            to={to}
            tabIndex={-1}
            className={(state) =>
              `${linkClassName(state)} min-w-0 flex-1 text-[12px] [@media(pointer:coarse)]:min-h-touch`
            }
            onClick={onNavigate}
          >
            {row.hasChildren ? (
              <NavFoldGlyph
                icon={
                  <PageIcon
                    name={page.icon}
                    className={iconClassName}
                    accentPosition={accentPosition}
                  />
                }
                expanded={row.expanded}
                name={title}
                sizeClassName={iconClassName}
                onToggle={onToggle}
                dataAttributes={{ 'data-page-caret': page.id }}
              />
            ) : (
              <PageIcon
                name={page.icon}
                className={iconClassName}
                accentPosition={accentPosition}
              />
            )}
            <span data-cut="name" className="truncate">
              {titleWaiting ? <Skeleton className="inline-block h-3 w-24 align-middle" /> : title}
            </span>
          </NavLink>
        </div>
        {manageControls ? (
          <>
            <button
              type="button"
              data-page-add-child={page.id}
              aria-label={t('navigation.sidebar.newPageUnder', { name: title })}
              onClick={onCreateChild}
              {...gateControlProps(manageGate, {
                title: t('navigation.sidebar.newPageUnder', { name: title }),
              })}
              className="page-drag-handle mr-1 grid h-7 w-4 shrink-0 cursor-pointer place-items-center rounded text-gray-400 outline-none hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:text-gray-500 dark:hover:text-gray-200 [@media(pointer:coarse)]:mr-0 [@media(pointer:coarse)]:h-touch [@media(pointer:coarse)]:w-touch"
            >
              <Plus className="h-3 w-3 [@media(pointer:coarse)]:touch-glyph" />
            </button>
            <button
              type="button"
              data-page-handle={page.id}
              aria-label={t('navigation.sidebar.reorder', { name: title })}
              // The handle sits at the end of the row for every pointer (F-0201): it keeps its
              // hover-and-focus reveal (`.page-drag-handle` in index.css) and its 44px target where
              // the pointer is coarse and there is no hover.
              className={`page-drag-handle grid h-7 w-4 shrink-0 cursor-grab place-items-center rounded text-gray-400 outline-none hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 active:cursor-grabbing aria-disabled:cursor-default dark:text-gray-500 dark:hover:text-gray-200 [@media(pointer:coarse)]:h-touch [@media(pointer:coarse)]:w-touch ${
                isDragging ? 'page-drag-handle--visible' : ''
              }`}
              // Refused, the drag instructions would be the description a screen reader reads, in
              // place of the reason; there is no drag to describe.
              {...(manageGate.state === 'refused' ? {} : attributes)}
              {...(manageGate.state === 'refused' ? {} : listeners)}
              aria-disabled={reorderDisabled}
              onKeyDown={onHandleKeyDown}
              {...gateControlProps(manageGate, {
                title: t('navigation.sidebar.dragPageHint'),
              })}
            >
              <GripVertical className="h-3.5 w-3.5 [@media(pointer:coarse)]:touch-glyph" />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

/** `items` with the entry at `from` moved to `to` (dnd-kit's `arrayMove`, without loading dnd-kit). */
function movedTo<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as T);
  return next;
}
